import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TEMPLATES, CATEGORIES, EXPERIENCE_LEVELS, PAGE_OPTIONS, filterTemplates } from "../lib/templates";
import TemplateRenderer, { SAMPLE_DATA } from "../components/TemplateRenderer";
import { Filter, X, ShieldCheck, Sparkles, Check, Layers } from "lucide-react";

export default function ResumeTemplates() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cvId = params.get("cvId");
  const isPremium = user?.subscription === "premium" || user?.is_admin;

  const [category, setCategory] = useState("All");
  const [expLevel, setExpLevel] = useState("");
  const [atsOnly, setAtsOnly] = useState(false);
  const [pages, setPages] = useState(0); // 0 = any
  const [industry, setIndustry] = useState("All");
  const [preview, setPreview] = useState(null); // template object

  const industries = useMemo(() => {
    const set = new Set();
    TEMPLATES.forEach((t) => (t.industries || []).forEach((i) => set.add(i)));
    return ["All", ...Array.from(set).sort()];
  }, []);

  const filtered = useMemo(
    () => filterTemplates({ category, expLevel: expLevel || undefined, atsOnly, pages: pages || undefined, industry }),
    [category, expLevel, atsOnly, pages, industry]
  );

  const applyTemplate = async (tpl) => {
    if (!cvId) {
      // No target CV in URL — go to builder and pick from Settings
      toast.message("Open a CV first from the Resume Builder, then pick a template.");
      nav("/create-cv");
      return;
    }
    try {
      const cur = await api.get(`/cv/${cvId}`);
      const data = cur.data.data || {};
      const settings = { ...(data.settings || {}), template_id: tpl.id };
      await api.patch(`/cv/${cvId}`, { data: { ...data, settings } });
      toast.success(`Template "${tpl.name}" applied to your CV`);
      nav(`/cv-builder/${cvId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply template");
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#80FFEA]">Resume Templates</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">
          Pick a template that fits the role.
        </h1>
        <p className="text-slate-400 mt-2 max-w-2xl text-sm">
          {TEMPLATES.length}+ Cavilow-designed templates. All originals — never copied from other providers. Switch anytime; your CV data stays the same.
        </p>
      </div>

      {/* Filter bar */}
      <div className="glass p-5 fade-up-delay-1" data-testid="template-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-slate-400" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Filters</span>
          <span className="ml-auto text-xs text-slate-400">{filtered.length} of {TEMPLATES.length} shown</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Select label="Category" value={category} onChange={setCategory} options={["All", ...CATEGORIES]} testid="filter-category" />
          <Select label="Experience" value={expLevel} onChange={setExpLevel} options={[{ v: "", l: "Any" }, ...EXPERIENCE_LEVELS.map((l) => ({ v: l, l: l[0].toUpperCase() + l.slice(1) }))]} testid="filter-exp" />
          <Select label="Length" value={pages} onChange={(v) => setPages(Number(v))} options={[{ v: 0, l: "Any" }, ...PAGE_OPTIONS.map((p) => ({ v: p, l: `${p} page${p > 1 ? "s" : ""}` }))]} testid="filter-pages" />
          <Select label="Industry" value={industry} onChange={setIndustry} options={industries} testid="filter-industry" />
          <label className="flex items-end gap-2 cursor-pointer">
            <div className="flex items-center gap-2 rounded-xl border-2 border-white/10 px-3 py-2.5 w-full">
              <input type="checkbox" checked={atsOnly} onChange={(e) => setAtsOnly(e.target.checked)} className="accent-[var(--cavilo-blue)]" data-testid="filter-ats" />
              <span className="text-sm font-bold text-slate-200 inline-flex items-center gap-1"><ShieldCheck size={14} className="text-[#80FFEA]" /> ATS-friendly only</span>
            </div>
          </label>
        </div>
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 fade-up-delay-2">
        {filtered.map((t) => (
          <TemplateCard key={t.id} template={t} onPreview={() => setPreview(t)} onApply={() => applyTemplate(t)} isPremium={isPremium} />
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full rounded-3xl border-2 border-dashed border-white/10 p-10 text-center text-slate-400 text-sm">
            No templates match those filters. Try loosening them.
          </div>
        )}
      </div>

      {/* Preview modal */}
      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} onApply={() => { applyTemplate(preview); setPreview(null); }} />}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  const opts = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border-2 border-white/10 px-3 py-2.5 text-sm bg-white focus:border-[var(--cavilo-blue)] outline-none"
      >
        {opts.map((o) => <option key={String(o.v)} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

function TemplateCard({ template, onPreview, onApply }) {
  return (
    <div
      data-testid={`template-card-${template.id}`}
      className="group glass hover:border-[var(--cavilo-blue)] hover:shadow-[0_20px_50px_-25px_rgba(30,111,230,0.45)] transition-all overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[3/4] bg-white/5 overflow-hidden border-b border-white/8">
        <div className="absolute inset-0 p-3 overflow-hidden">
          <div className="scale-[0.36] origin-top-left w-[278%]">
            <TemplateRenderer templateId={template.id} data={SAMPLE_DATA} />
          </div>
        </div>
        <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
          {template.ats_friendly && <Badge icon={ShieldCheck} label="ATS" tone="emerald" />}
          {template.pages > 1 && <Badge icon={Layers} label={`${template.pages}p`} tone="slate" />}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display font-bold text-[15px] text-white">{template.name}</div>
          <span className="w-4 h-4 rounded-full shrink-0" style={{ background: template.accent }} />
        </div>
        <div className="mt-1 flex flex-wrap gap-1">
          {template.categories.slice(0, 2).map((c) => (
            <span key={c} className="text-[10px] font-bold text-slate-400 bg-white/[0.06] rounded-full px-2 py-0.5">{c}</span>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            data-testid={`template-preview-${template.id}`}
            onClick={onPreview}
            className="rounded-xl border-2 border-white/10 text-slate-200 hover:border-[var(--cavilo-ink)] px-3 py-2 text-xs font-bold"
          >
            Preview
          </button>
          <button
            data-testid={`template-apply-${template.id}`}
            onClick={onApply}
            className="rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-3 py-2 text-xs font-bold btn-press inline-flex items-center justify-center gap-1"
          >
            <Check size={12} /> Use
          </button>
        </div>
      </div>
    </div>
  );
}

function Badge({ icon: Icon, label, tone }) {
  const tones = {
    emerald: "bg-emerald-500 text-white",
    slate: "bg-slate-800 text-white",
    amber: "bg-amber-500 text-white",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full ${tones[tone] || tones.slate} px-2 py-0.5 text-[10px] font-bold`}>
      <Icon size={10} /> {label}
    </span>
  );
}

function PreviewModal({ template, onClose, onApply }) {
  return (
    <div
      data-testid="template-preview-modal"
      className="fixed inset-0 z-[80] grid place-items-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[var(--cavilo-ink)]/70 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-3xl shadow-2xl border border-white/10 max-w-3xl w-full my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div>
            <div className="font-display font-bold text-lg text-white">{template.name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{template.categories.join(" · ")}</div>
          </div>
          <button onClick={onClose} className="grid place-items-center w-9 h-9 rounded-xl text-slate-400 hover:bg-white/[0.06]">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 bg-white/5 max-h-[70vh] overflow-y-auto">
          <div className="mx-auto max-w-[720px] bg-white rounded-2xl shadow-lg p-8">
            <TemplateRenderer templateId={template.id} data={SAMPLE_DATA} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-white/8">
          <button onClick={onClose} className="rounded-xl border-2 border-white/10 text-slate-200 px-4 py-2 text-sm font-bold">Close</button>
          <button
            onClick={onApply}
            data-testid="template-preview-apply"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-5 py-2 text-sm font-bold btn-press"
          >
            <Sparkles size={14} /> Use this template
          </button>
        </div>
      </div>
    </div>
  );
}
